import { TrialCountdownIndicator } from "@components/sidebar/trial-countdown/trial-countdown-indicator";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { ListItemButton } from "@mui/material";
import { MainRoutes, SettingsTabs } from "@router/route-params";
import { DateTimeUtils } from "@stellar/utils";
import { topPriorityXgSubscriptionSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { blue, neutral } from "@flat-ui";
import { isTrialSubscription } from "@utils/subscription-utils";
import { useMemo } from "react";
import { NavLink } from "react-router-dom";

/** Shows the remaining days of a trial subscription */
export function TrialCountdown(): JSX.Element | null {
  const subscription = useAppSelector(topPriorityXgSubscriptionSelector);
  const { canViewSubscriptionDetails } = useHasUserValidRoleCompanyLevel();
  const isTrial = isTrialSubscription(subscription);
  const remainingDays = useMemo(() => {
    if (!subscription || !isTrial) {
      return;
    }
    const expirationDate = subscription.endDate;
    const diffFromTodayInDays = DateTimeUtils.diffFromTodayInDays({
      date: expirationDate,
      shouldIncludeToday: false,
    });
    return diffFromTodayInDays > 0 ? diffFromTodayInDays : 0;
  }, [subscription, isTrial]);

  if (remainingDays === undefined) {
    return null;
  }

  return canViewSubscriptionDetails ? (
    <NavLink
      to={`${MainRoutes.settings}/${SettingsTabs.subscription}`}
      style={{
        textDecoration: "none",
      }}
    >
      <ListItemButton
        sx={{
          padding: 0,
          "&:hover": {
            backgroundColor: neutral[200],
            color: blue[500],
            fill: blue[500],
          },
        }}
      >
        <TrialCountdownIndicator remainingDays={remainingDays} />
      </ListItemButton>
    </NavLink>
  ) : (
    <TrialCountdownIndicator remainingDays={remainingDays} />
  );
}
